/* eslint-disable import/prefer-default-export */
import * as R from 'ramda';
import { generateListTags, generateTag } from 'utils/helpers/fetch';
import dispatchingApi from '../dispatchingApi';

// example mock
/*
queryFn: ({ params }) =>
  import('./mockData/viewTrailerDetailsByLocation').then(({ default: getTrailers }) => ({
    data: getTrailers(params),
  })),
*/

const getUrl = R.concat('loads/');
const getLoadingUrl = R.concat(
  `${process.env.REACT_APP_PRODUCT_LOADING_BASE_URL}/dispatching-carrier-authorizations/`
);

const generateLoadsTag = (result, eroor, { load }) =>
  [
    ...generateTag('Driver', 'driverId', load),
    ...generateTag('Location', 'locationId', load),
    ...generateTag('Trailer', 'trailerId', load),
    { id: 'LIST', type: 'Date' },
  ]
    .concat(
      R.either(R.equals('Draft'), R.equals('DRAFT'))(load.status)
        ? [{ id: 'LIST', type: 'Load' }]
        : load.loadId
        ? generateTag('Load', 'loadId', load)
        : [{ id: 'LIST', type: 'Load' }]
    )
    .concat(
      Object.values(load.compartments).flatMap(c =>
        generateTag('Destination', 'destinationId', c.destination)
      )
    );

const loadsEndpoints = dispatchingApi.injectEndpoints({
  endpoints: ({ query, mutation }) => ({
    addLoadToFavorites: mutation({
      invalidatesTags: (result, error, { loadId: id }) => [{ id, type: 'Load' }],
      query: ({ loadId }) => ({
        body: { loadId },
        method: 'POST',
        url: getUrl('add-load-to-favorites'),
      }),
    }),

    createDraftLoad: mutation({
      invalidatesTags: generateLoadsTag,
      query: ({ load }) => ({
        body: load,
        method: 'POST',
        url: getUrl('create-draft-load'),
      }),
    }),

    createLoad: mutation({
      invalidatesTags: generateLoadsTag,
      query: ({ load }) => ({
        body: load,
        method: 'POST',
        url: getUrl('create-load'),
      }),
    }),

    deleteLoad: mutation({
      invalidatesTags: (result, error, { load }) => [
        { id: load.loadId, type: 'Load' },
        { id: 'LIST', type: 'Driver' },
        { id: 'LIST', type: 'Location' },
        { id: 'LIST', type: 'Date' },
        { id: 'LIST', type: 'Destination' },
      ],
      query: ({ load }) => ({
        body: { loadId: load.loadId },
        method: 'POST',
        url: getUrl('delete-load'),
      }),
    }),

    editLoad: mutation({
      invalidatesTags: generateLoadsTag,
      query: ({ load }) => ({
        body: load,
        method: 'POST',
        url: getUrl('edit-load'),
      }),
    }),

    getAdditives: query({
      providesTags: (result, error, { productGradeCode, supplierIds }) => [
        ...generateListTags('Additive', 'additiveId', result),
        ...supplierIds.map(id => ({ id, type: 'Supplier' })),
        { id: productGradeCode, type: 'Recipe' },
      ],
      query: ({ locationId, productGradeCode, supplierIds }) => ({
        params: { locationId, productGradeCode, supplierIds },
        url: getLoadingUrl('get-additives'),
      }),
    }),

    getCustomRecipeAllocation: query({
      query: ({ locationId, productGradeCode, supplierId }) => ({
        params: { locationId, productGradeCode, supplierId },
        url: getLoadingUrl('get-custom-recipe-allocation'),
      }),
    }),

    getDestinations: query({
      providesTags: generateListTags('Destination', 'destinationId'),
      query: ({ loadingControlIds, locationId }) => ({
        params: { loadingControlIds, locationId },
        url: getLoadingUrl('get-destinations'),
      }),
    }),

    getDrivers: query({
      providesTags: generateListTags('Driver', 'driverId'),
      query: ({ loadingControlIds, locationId }) => ({
        params: { loadingControlIds, locationId },
        url: getUrl('get-drivers'),
      }),
    }),

    getHouseRecipeAllocation: query({
      query: ({ locationId, productGradeCode, products }) => ({
        body: { locationId, productGradeCode, products },
        method: 'POST',
        url: getLoadingUrl('get-house-recipe-allocation'),
      }),
    }),

    getLoadTypes: query({
      providesTags: generateListTags('LoadType', 'loadTypeId'),
      queryFn: ({ locationId }) =>
        import('./mockData/getLoadTypes').then(({ default: getLoadTypes }) => ({
          data: getLoadTypes(locationId),
        })),
    }),

    getLoadingControlsBySupplier: query({
      providesTags: (result, error, { supplierId, locationId, productId }) =>
        [
          ...generateListTags('LoadingControl', 'loadingControlId', result),
          { id: supplierId, type: 'Supplier' },
          { id: locationId, type: 'Location' },
        ].concat(productId ? [{ id: productId, type: 'Product' }] : []),
      query: ({ supplierId, locationId, productGradeCode }) => ({
        params: { locationId, productGradeCode, supplierId },
        url: getLoadingUrl('get-loading-controls-by-supplier'),
      }),
    }),

    getLocations: query({
      providesTags: generateListTags('Location', 'locationId'),
      query: () => ({
        url: getUrl('get-locations'),
      }),
    }),

    // getLoadingUrl
    getProductsByRecipe: query({
      providesTags: (result, error, { recipeId: id }) => [
        ...generateListTags('Product', 'productId', result),
        { id, type: 'Recipe' },
      ],
      queryFn: ({ recipeId }) =>
        import('./mockData/getProductsByRecipe').then(({ default: getProductsByRecipe }) => ({
          data: getProductsByRecipe({ recipeId }),
        })),
    }),

    // getLoadingUrl
    getProductsBySupplier: query({
      providesTags: (result, error, { supplierId: id }) => [
        ...generateListTags('Product', 'productId', result),
        { id, type: 'supplierId' },
      ],
      queryFn: ({ supplierId }) =>
        import('./mockData/getProductsBySupplier').then(({ default: getProductsBySupplier }) => ({
          data: getProductsBySupplier({ supplierId }),
        })),
    }),

    getRecipes: query({
      providesTags: result => [
        ...generateListTags('Recipe', 'recipeId', result),
        ...result.flatMap(r =>
          Object.values(r.products).flatMap(generateTag('Product', 'productId'))
        ),
      ],
      query: ({ locationId }) => ({
        params: { locationId },
        url: getLoadingUrl('get-recipes'),
      }),
    }),

    getRecipesBySupplier: query({
      providesTags: (result, error, { locationId, supplierId }) => [
        ...generateListTags('Recipe', 'recipeId', result),
        { id: locationId, type: 'Location' },
        { id: supplierId, type: 'Supplier' },
      ],
      query: ({ locationId, supplierId }) => ({
        params: { locationId, supplierId },
        url: getLoadingUrl('get-recipes-by-supplier'),
      }),
    }),

    getSuppliers: query({
      providesTags: generateListTags('Supplier', 'supplierId'),
      query: ({ locationId }) => ({
        params: { locationId },
        url: getLoadingUrl('get-suppliers'),
      }),
    }),

    getSuppliersByProduct: query({
      providesTags: (result, error, { locationId, productId }) => [
        ...generateListTags('Supplier', 'supplierId', result),
        { id: locationId, type: 'Location' },
        { id: productId, type: 'Product' },
      ],
      query: ({ locationId, productGradeCode }) => ({
        params: { locationId, productGradeCode },
        url: getLoadingUrl('get-suppliers-by-product'),
      }),
    }),

    getTrailers: query({
      providesTags: generateListTags('Trailer', 'trailerId'),
      query: ({ locationId }) => ({
        params: { locationId },
        url: getUrl('get-trailers'),
      }),
    }),

    // getLoadingUrl
    getUnloadAllocation: query({
      queryFn: ({ supplierId, locationId, productId }) =>
        import('./mockData/getUnloadAllocation').then(({ default: getAllocation }) => ({
          data: getAllocation({ locationId, productId, supplierId }),
        })),
    }),

    removeLoadFromFavorites: mutation({
      invalidatesTags: (result, error, { loadId: id }) => [{ id, type: 'Load' }],
      query: ({ loadId }) => ({
        body: { loadId },
        method: 'POST',
        url: getUrl('remove-load-from-favorites'),
      }),
    }),

    validatePoNumber: query({
      query: ({ poNumber }) => ({
        params: { poNumber },
        url: getUrl('validate-po-number'),
      }),
    }),

    viewLoad: query({
      providesTags: result => [
        { id: result?.loadId, type: 'Load' },
        { id: result.driver?.driverId, type: 'Driver' },
        { id: result.trailer?.trailerId, type: 'Trailer' },
      ],
      query: ({ driver, loadId }) => ({
        params: { driver, loadId },
        url: getUrl('view-load'),
      }),
      transformResponse: r => ({
        ...r,
        compartments: R.map(
          R.evolve({
            capacityInGallons: R.ifElse(
              () => r.fillAll,
              () => r.trailer.compartments.reduce((t, { capacityInGallons: c }) => t + c, 0),
              R.identity
            ),
            volume: R.ifElse(R.isNil, R.always(''), v => v.toLocaleString()),
          }),
          r.compartments
        ),
      }),
    }),

    viewLoadsDetailsByDate: query({
      providesTags: (result, error, { createdDate: id }) => [
        ...generateListTags('Load', 'load.loadId', result),
        ...generateListTags('Driver', 'driver.driverId', result),
        { id, type: 'Date' },
      ],
      query: ({ createdDate }) => ({
        params: { createdDate: encodeURIComponent(createdDate) },
        url: getUrl('view-details-by-date'),
      }),
    }),

    viewLoadsDetailsByDestination: query({
      providesTags: (result, error, { destinationId: id }) => [
        ...generateListTags('Load', 'load.loadId', result),
        ...generateListTags('Location', 'location.locationId', result),
        { id, type: 'Destination' },
      ],
      query: ({ destinationId }) => ({
        params: { destinationId },
        url: getUrl('view-details-by-destination'),
      }),
    }),

    viewLoadsDetailsByDriver: query({
      providesTags: (result, error, { driverId: id }) => [
        ...generateListTags('Load', 'load.loadId', result),
        ...generateListTags('Location', 'location.locationId', result),
        ...generateListTags('Trailer', 'trailer.trailerId', result),
        { id, type: 'Driver' },
      ],
      query: ({ driverId }) => ({
        params: { driverId },
        url: getUrl('view-details-by-driver'),
      }),
    }),

    viewLoadsDetailsByLocation: query({
      providesTags: (result, error, { locationId: id }) => [
        ...generateListTags('Load', 'load.loadId', result),
        ...generateListTags('Driver', 'driver.driverId', result),
        ...generateListTags('Trailer', 'trailer.trailerId', result),
        { id, type: 'Location' },
      ],
      query: ({ locationId }) => ({
        params: { locationId },
        url: getUrl('view-details-by-location'),
      }),
    }),

    viewLoadsSummaryByDate: query({
      providesTags: generateListTags('Date', 'createdDate'),
      query: () => ({
        url: getUrl('view-summary-by-date'),
      }),
    }),

    viewLoadsSummaryByDestination: query({
      providesTags: generateListTags('Destination', 'destinationId'),
      query: () => ({
        url: getUrl('view-summary-by-destination'),
      }),
    }),

    viewLoadsSummaryByDriver: query({
      providesTags: generateListTags('Driver', 'driverId'),
      query: () => ({
        url: getUrl('view-summary-by-driver'),
      }),
    }),

    viewLoadsSummaryByLocation: query({
      providesTags: generateListTags('Location', 'locationId'),
      query: () => ({
        url: getUrl('view-summary-by-location'),
      }),
    }),
  }),
});

export const {
  useAddLoadToFavoritesMutation,
  useCreateDraftLoadMutation,
  useCreateLoadMutation,
  useDeleteLoadMutation,
  useEditLoadMutation,
  useLazyGetAdditivesQuery,
  useGetDestinationsQuery,
  useGetDriversQuery,
  useGetLoadingControlsBySupplierQuery,
  useGetLoadTypesQuery,
  useGetLocationsQuery,
  useGetProductsByRecipeQuery,
  useGetProductsBySupplierQuery,
  useGetRecipesQuery,
  useGetRecipesBySupplierQuery,
  useGetSuppliersQuery,
  useGetSuppliersByProductQuery,
  useGetTrailersQuery,
  useLazyGetCustomRecipeAllocationQuery,
  useLazyGetHouseRecipeAllocationQuery,
  useLazyGetUnloadAllocationQuery,
  useLazyValidatePoNumberQuery,
  useRemoveLoadFromFavoritesMutation,
  useViewLoadQuery,
  useViewLoadsDetailsByDestinationQuery,
  useViewLoadsDetailsByDateQuery,
  useViewLoadsDetailsByDriverQuery,
  useViewLoadsDetailsByLocationQuery,
  useViewLoadsSummaryByDestinationQuery,
  useViewLoadsSummaryByDateQuery,
  useViewLoadsSummaryByDriverQuery,
  useViewLoadsSummaryByLocationQuery,
} = loadsEndpoints;
